import {
  Box,
  Divider,
  FlexBox,
  Grid,
  Text,
  TextInline,
} from '@trmediaab/zebra';
import { objectKeys } from '@trmediaab/zebra-utils';
import { Fragment, useCallback, useState } from 'react';

import { parseBridle, parseCarriage, parseShoes } from '~/utils/equipment';
import { formatOdds } from '~/utils/numbers';

import { DistanceRow } from '.';
import { Bridle } from './Bridle';
import { Carriage } from './Carriage';
import {
  renderDriver,
  renderGamePercentage,
  renderHorseName,
  renderHorseNumber,
  renderTrackInfo,
} from './columns';
import Expanded from './Expanded';
import { Shoes } from './Shoes';
import { setShouldExpandStart, shouldExpandStart } from './utils';

const HeaderRow = () => (
  <Box
    display="contents"
    sx={{
      '> *': {
        bg: 'brands.travrondenSpel',
        gridRow: 'span 2',
        py: 3,
        fontSize: 0,
        color: 'white',
        fontWeight: 'bold',
      },
      '> *:first-of-type': { pl: 3 },
      '> *:last-of-type': { pr: 3 },
    }}
  >
    <Text>NR</Text>
    <Text>HÄST/ KUSK (TRÄNARE)</Text>
    <Text textAlign="right">DIST:SPÅR</Text>
  </Box>
);

const StartRow = ({ start, betType, isFirst, hasPremium }) => {
  const [expanded, setExpanded] = useState(isFirst && shouldExpandStart());
  const hasInterview = start.interviews && start.interviews.length > 0;
  const bridle = parseBridle(start);

  const handleExpand = useCallback(() => {
    setExpanded(prev => !prev);
    setShouldExpandStart();
  }, []);

  return (
    <Fragment>
      <Box
        onClick={handleExpand}
        rowGap="3"
        display="contents"
        sx={{
          cursor: 'pointer',
          '> *': { bg: 'white', '-webkit-tap-highlight-color': 'transparent' },
        }}
      >
        {/* Start Number */}
        <Box gridRow="span 2" py="3">
          {renderHorseNumber({ start, betType })}
        </Box>

        {/* Horse + Driver/Trainer */}
        <Box pr="1" pb="3" pt="20px">
          {renderHorseName({ start })}

          {renderDriver({ start, variant: 'mobile' })}
        </Box>

        {/* Distance/Track */}
        <Box py="3">
          {renderTrackInfo({
            start,
          })}
        </Box>

        {/* Additional Info */}
        <Grid
          gridColumn="2/-1"
          gridTemplateColumns="repeat(3 ,minmax(50px, 135px))"
          rowGap="1"
          pr="1"
          pb="3"
        >
          <Text fontSize="1">
            <TextInline color="brands.travrondenSpel">Skor</TextInline>
            &nbsp;&nbsp;
            <Shoes
              data={parseShoes(start)}
              display="inline"
              fontWeight="bold"
            />
          </Text>

          <Text fontSize="1">
            <TextInline color="brands.travrondenSpel">Vagn</TextInline>
            &nbsp;&nbsp;
            <Carriage
              data={parseCarriage(start)}
              display="inline"
              fontWeight="bold"
            />
          </Text>

          <Text />

          {betType && (
            <Text fontSize="1" color="brands.travrondenSpel">
              <FlexBox>
                <TextInline>{betType}-%</TextInline>&nbsp;&nbsp;
                {renderGamePercentage({
                  start,
                  betType,
                })}
              </FlexBox>
            </Text>
          )}

          {betType !== null && (
            <Text fontSize="1" color="brands.travrondenSpel">
              <TextInline>Trend</TextInline>&nbsp;&nbsp;
              <TextInline fontWeight="bold">
                {start.game_percent.providers?.ATG?.[betType]?.trend > 0
                  ? '+'
                  : ''}
                {formatOdds(
                  start.game_percent.providers?.ATG?.[betType]?.trend,
                  1,
                  1,
                )}
              </TextInline>
            </Text>
          )}

          <Text fontSize="1" color="brands.travrondenSpel">
            <TextInline>V-odds</TextInline>&nbsp;&nbsp;
            <TextInline fontWeight="bold">
              {formatOdds(start.odds.providers.ATG?.['V'])}
            </TextInline>
          </Text>

          {hasInterview && (
            <FlexBox flexDirection="column" gap="2" gridColumn="1/-1" pr="2,5">
              <Text
                fontSize="1"
                fontWeight="semiBold"
                color="text.tertiary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                <TextInline color="brands.travrondenSpel">
                  Intervju -{' '}
                </TextInline>
                <TextInline fontStyle="italic">
                  {start.interviews[0].teaser}
                </TextInline>
              </Text>

              {bridle.value != null && hasPremium && (
                <Text
                  fontSize="1"
                  fontWeight="semiBold"
                  color="text.tertiary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  <TextInline color="brands.travrondenSpel">
                    Huvudlag -{' '}
                  </TextInline>
                  <Bridle data={bridle} fontSize="1" display="inline" />
                </Text>
              )}
            </FlexBox>
          )}
        </Grid>
      </Box>

      {expanded && (
        <Box gridColumn="1/-1">
          <Expanded start={start} betType={betType} />
        </Box>
      )}
    </Fragment>
  );
};

const StartlistCompact = ({ startsByDistance, betType, hasPremium }) => (
  <Grid gridTemplateColumns="max-content 229px 1fr" gridAutoRows="auto">
    <HeaderRow />
    {objectKeys(startsByDistance).map((distance, distIdx) => (
      <Fragment key={distance}>
        {Number(distance) > 0 && <DistanceRow distance={distance} />}
        {startsByDistance[distance].map((start, idx, array) => (
          <Fragment key={start.id}>
            <StartRow
              start={start}
              betType={betType}
              isFirst={distIdx === 0 && idx === 0}
              hasPremium={hasPremium}
            />

            {idx !== array.length - 1 && (
              <Divider gridColumn="1/-1" variant="startlist" />
            )}
          </Fragment>
        ))}
      </Fragment>
    ))}
  </Grid>
);

export default StartlistCompact;
